import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUOTE_RECIPIENT = "thekingstoncoffeeco@gmail.com";

const EVENT_TYPES = [
  "Wedding",
  "Corporate Event",
  "Private Party",
  "Birthday / Celebration",
  "Community / Public Event",
  "Other",
];

const empty = {
  name: "",
  email: "",
  phone: "",
  event_type: "",
  event_date: "",
  location: "",
  guest_count: "",
  message: "",
};

export const QuoteForm = () => {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [mailtoLink, setMailtoLink] = useState("");

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.event_type) {
      toast.error("Please fill in your name, email, and event type.");
      return;
    }
    setSubmitting(true);

    // Keep a backup copy of the lead (best-effort, non-blocking)
    try {
      await axios.post(`${API}/quote-requests`, form);
    } catch (err) {
      console.error(err);
    }

    // Build an email the visitor sends from their own mail app — no server involved
    const subjectParts = ["Quote Request", form.event_type];
    if (form.event_date) subjectParts.push(`on ${form.event_date}`);
    const subject = subjectParts.join(" — ");
    const lines = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.phone && `Phone: ${form.phone}`,
      `Event Type: ${form.event_type}`,
      form.event_date && `Event Date: ${form.event_date}`,
      form.location && `Location: ${form.location}`,
      form.guest_count && `Estimated Guests: ${form.guest_count}`,
      "",
      form.message && `Details:\n${form.message}`,
    ].filter(Boolean);
    const link = `mailto:${QUOTE_RECIPIENT}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(lines.join("\n"))}`;

    setMailtoLink(link);
    window.location.href = link;

    setForm(empty);
    setDone(true);
    setSubmitting(false);
  };

  return (
    <section id="quote" data-testid="quote-section" className="py-24 md:py-32 bg-[#F2EBE1]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5E3C] mb-4">
            Let's Plan Your Event
          </p>
          <h2 className="font-serif-display text-4xl md:text-5xl font-semibold text-[#2C2621] tracking-tight">
            Request a quote
          </h2>
          <p className="mt-5 text-[#594D42] text-lg leading-relaxed max-w-xl mx-auto">
            Share a few details and we'll craft a custom proposal for your event. Submitting
            opens your email app with everything ready — just press send.
          </p>
        </div>

        {done ? (
          <div
            data-testid="quote-success"
            className="bg-white border border-[#E5DFD3] rounded-lg shadow-sm p-10 md:p-14 text-center"
          >
            <CheckCircle2 size={56} strokeWidth={1.5} className="text-[#8B5E3C] mx-auto mb-5" />
            <h3 className="font-serif-display text-3xl font-semibold text-[#2C2621] mb-3">
              Almost there!
            </h3>
            <p className="text-[#594D42] text-lg leading-relaxed max-w-md mx-auto">
              Your email app should have opened with your request ready to go — just press
              <strong> Send</strong> and we'll be in touch within 1–2 business days.
            </p>
            {mailtoLink && (
              <a
                href={mailtoLink}
                data-testid="quote-mailto-fallback"
                className="mt-7 inline-block bg-[#8B5E3C] text-white rounded-md px-7 py-3.5 font-semibold transition-all duration-300 hover:bg-[#70482B] hover:-translate-y-0.5 hover:shadow-md"
              >
                Didn't open? Click to send your email
              </a>
            )}
            <div>
              <button
                data-testid="quote-reset"
                onClick={() => setDone(false)}
                className="mt-6 text-[#8B5E3C] font-semibold hover:text-[#70482B] transition-colors"
              >
                Submit another request
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="bg-white border border-[#E5DFD3] rounded-lg shadow-sm p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <Field label="Full Name *">
              <Input data-testid="quote-name" value={form.name} onChange={set("name")} placeholder="Jane Smith" className="border-[#E5DFD3] focus-visible:ring-[#8B5E3C] bg-[#FAF7F2]" />
            </Field>
            <Field label="Email *">
              <Input data-testid="quote-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@email.com" className="border-[#E5DFD3] focus-visible:ring-[#8B5E3C] bg-[#FAF7F2]" />
            </Field>
            <Field label="Phone">
              <Input data-testid="quote-phone" value={form.phone} onChange={set("phone")} placeholder="(360) 555-0123" className="border-[#E5DFD3] focus-visible:ring-[#8B5E3C] bg-[#FAF7F2]" />
            </Field>
            <Field label="Event Type *">
              <Select value={form.event_type} onValueChange={set("event_type")}>
                <SelectTrigger data-testid="quote-event-type" className="border-[#E5DFD3] focus:ring-[#8B5E3C] bg-[#FAF7F2]">
                  <SelectValue placeholder="Select event type" />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`event-option-${t.toLowerCase().split(" ")[0]}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Event Date">
              <Input data-testid="quote-date" type="date" value={form.event_date} onChange={set("event_date")} className="border-[#E5DFD3] focus-visible:ring-[#8B5E3C] bg-[#FAF7F2]" />
            </Field>
            <Field label="Estimated Guests">
              <Input data-testid="quote-guests" value={form.guest_count} onChange={set("guest_count")} placeholder="e.g. 75" className="border-[#E5DFD3] focus-visible:ring-[#8B5E3C] bg-[#FAF7F2]" />
            </Field>
            <Field label="Event Location" className="md:col-span-2">
              <Input data-testid="quote-location" value={form.location} onChange={set("location")} placeholder="Venue or city in Puget Sound" className="border-[#E5DFD3] focus-visible:ring-[#8B5E3C] bg-[#FAF7F2]" />
            </Field>
            <Field label="Tell us about your event" className="md:col-span-2">
              <Textarea data-testid="quote-message" value={form.message} onChange={set("message")} rows={4} placeholder="Signature drinks, timing, special requests..." className="border-[#E5DFD3] focus-visible:ring-[#8B5E3C] bg-[#FAF7F2] resize-none" />
            </Field>

            <div className="md:col-span-2">
              <button
                type="submit"
                data-testid="quote-submit"
                disabled={submitting}
                className="w-full bg-[#8B5E3C] text-white rounded-md px-8 py-4 text-base font-semibold transition-all duration-300 hover:bg-[#70482B] hover:-translate-y-0.5 hover:shadow-md disabled:opacity-70 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" /> Sending...
                  </>
                ) : (
                  "Send My Request"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-2 ${className}`}>
    <Label className="text-sm font-semibold text-[#2C2621]">{label}</Label>
    {children}
  </div>
);
